import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAIChat } from '../config/aiChatContext';
import { suggestSite, popularSearches, logSuggestionClick } from '../utils/searchClient';

const popularCache = {};

const loadPopular = (language) => {
  popularCache[language] = popularCache[language] || popularSearches(language).catch(() => { delete popularCache[language]; return []; });
  return popularCache[language];
};

export const askAIPrompt = (q, isAr) => (isAr ? `أبحث في الموقع عن: ${q} — هل تقدر تساعدني؟` : `I'm looking for "${q}" on the site — can you help?`);

/** Shared state + handlers for the header search box and the mobile overlay. */
export function useSearchBox({ language, onDone }) {
  const isAr = language === 'ar';
  const navigate = useNavigate();
  const { openChat } = useAIChat();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [queries, setQueries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState([]);
  const [active, setActive] = useState(-1);
  const reqId = useRef(0);

  useEffect(() => {
    let alive = true;
    loadPopular(language).then((p) => { if (alive) setPopular(p); });
    return () => { alive = false; };
  }, [language]);

  useEffect(() => {
    const term = q.trim();
    setActive(-1);
    if (!term) { setItems([]); setQueries([]); setTotal(0); setLoading(false); return undefined; }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(() => {
      suggestSite(term, language)
        .then((d) => { if (id === reqId.current) { setItems(d.items || []); setQueries(d.queries || []); setTotal(d.total || 0); } })
        .catch(() => { if (id === reqId.current) { setItems([]); setTotal(0); } })
        .finally(() => { if (id === reqId.current) setLoading(false); });
    }, 180);
    return () => clearTimeout(t);
  }, [q, language]);

  const finish = useCallback(() => { onDone?.(); }, [onDone]);

  const goSearch = useCallback((text, source = 'header') => {
    const term = (text ?? q).trim();
    if (!term) return;
    finish();
    navigate(`/search?q=${encodeURIComponent(term)}&src=${source}`);
  }, [q, navigate, finish]);

  const pick = useCallback((item, position) => {
    logSuggestionClick({ q: q.trim(), item, position, resultsCount: total, language });
    finish();
    navigate(item.url);
  }, [q, total, language, navigate, finish]);

  const askAI = useCallback(() => {
    const term = q.trim();
    logSuggestionClick({ q: term, item: { section: 'ai_assistant', id: null, title_ar: 'المساعد الذكي' }, position: null, resultsCount: 0, language });
    finish();
    openChat(askAIPrompt(term, isAr));
  }, [q, isAr, language, openChat, finish]);

  const noResults = !!q.trim() && !loading && items.length === 0;
  // Keyboard options: suggestions → "see all" → (ask AI when nothing found)
  const optionCount = items.length + 1 + (noResults ? 1 : 0);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % optionCount); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a <= 0 ? optionCount - 1 : a - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && active < items.length) pick(items[active], active);
      else if (active === items.length + 1 && noResults) askAI();
      else goSearch();
    } else if (e.key === 'Escape') { finish(); }
  };

  return { q, setQ, items, queries, total, loading, popular, active, setActive, noResults, goSearch, pick, askAI, onKeyDown };
}
